import type { GuideWalkthroughDef } from "../types";
import { explainFocusStep } from "./steps/shared";

/**
 * A guided tour of an OPEN transport order (the focus page,
 * `/recap_transport/:id`): read its status, understand the lines table, then
 * the handful of actions on a single order — delete a line, open its source
 * document, rename it, the order-level actions, and where the AI touchpoint
 * lives.
 *
 * Runs INSIDE an editable order — `requires` gates on `recap-focus-editable`
 * (PENDING / AI_PROPOSAL), the marker `PreOrderFocusView` renders only for a
 * modifiable order — so the rename/actions steps always resolve; on a confirmed
 * / exported order those controls are locked away and the tour would hunt for
 * absent targets.
 *
 * Every step is an EXPLAIN step on the focus page (see `explainFocusStep`): the
 * list route is a PREFIX, so an open order counts as on-route. The line-scoped
 * steps (`flow-row`, `flow-actions-delete`) are `optional` — a modifiable order
 * can legitimately have zero lines, and the overlay then skips them rather than
 * ending on a drift error.
 */
export const focusTransportTour: GuideWalkthroughDef = {
  id: "focus-transport-tour",
  titleKey: "guide.walkthroughs.focusTransportTour.title",
  requires: "recap-focus-editable",
  requiresBodyKey: "guide.requiresEditableOrder",
  steps: [
    // The status pill — the order's lifecycle at a glance.
    explainFocusStep(
      "recap-focus-status",
      "guide.walkthroughs.focusTransportTour.statusBody",
    ),
    // Rename the transport order — the inline title editor's trigger.
    explainFocusStep(
      "transport-title-section-trigger",
      "guide.walkthroughs.focusTransportTour.renameBody",
    ),
    // The column-header row — every column name describes its content.
    explainFocusStep(
      "recap-flows-header",
      "guide.walkthroughs.focusTransportTour.headerBody",
    ),
    // A single line = one position (one planned truck leg). `optional`: a
    // modifiable order can have zero lines.
    explainFocusStep(
      "flow-row",
      "guide.walkthroughs.focusTransportTour.positionBody",
      { optional: true },
    ),
    // How to delete a line — the row's delete control. `optional` for the same
    // zero-line reason; EXPLAIN only, so following the tour never drops a line.
    explainFocusStep(
      "flow-actions-delete",
      "guide.walkthroughs.focusTransportTour.deleteBody",
      { optional: true },
    ),
    // Open the order's source document — the blue source button (the email /
    // attachment it came from), NOT the Messages conversation toggle.
    explainFocusStep(
      "preorder-source-button",
      "guide.walkthroughs.focusTransportTour.openMessagesBody",
    ),
    // Order-level actions — delete or send to the ERP. The exact buttons vary
    // by state, so spotlight the always-present action cluster and name both
    // outcomes generically rather than a single state-specific button.
    explainFocusStep(
      "recap-focus-actions",
      "guide.walkthroughs.focusTransportTour.actionsBody",
    ),
    // Talk to Stockline about this order — the floating AI chat bubble.
    explainFocusStep(
      "floating-ai-button",
      "guide.walkthroughs.focusTransportTour.talkToStocklineBody",
    ),
  ],
};
